import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DocumentDto, HttpError } from '@myarvato/shared/models';
import { DetailViewDto, HandlingUnit, PackageDto } from '@myarvato/modules/transport/models';
import { DocumentsService } from '@myarvato/modules/transport/services';
import { UserTrackingService } from '@myarvato/core/services';
import { DocumentTableComponent } from '@myarvato/shared/documents';
import { Message } from 'primeng/message';
import { UploadDocumentDialogComponent } from '../upload-document-dialog/upload-document-dialog.component';
import { DetailsDocumentsStore } from './details-documents.store';
import { ButtonModule } from 'primeng/button';
import { getDialogResult, MyArvatoDialogService } from '@myarvato/shared/dialog';
import { TransportDocumentColumnsProvider } from '../../services/transport-document-column-provider.service';

@Component({
  selector: 'transport-details-documents',
  imports: [TranslatePipe, DocumentTableComponent, Message, ButtonModule],
  providers: [DetailsDocumentsStore],
  templateUrl: './details-documents.component.html',
  styleUrl: './details-documents.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsDocumentsComponent {
  // injections
  private readonly detailsDocumentsStore = inject(DetailsDocumentsStore);
  private readonly dialogService = inject(MyArvatoDialogService);
  private readonly documentColumnsProvider = inject(TransportDocumentColumnsProvider);
  private readonly documentsService = inject(DocumentsService);
  private readonly tracker = inject(UserTrackingService);
  private readonly translateService = inject(TranslateService);

  // inputs
  public readonly details = input.required<DetailViewDto>();

  // fields
  private readonly trackerCategory: string = 'transport-detail-documents';
  protected readonly showUploadDocumentButton = true;

  // Change Detection
  private readonly trackingNumber = computed(() => this.details().trackingNumber);
  protected readonly documentColumns = computed(() => this.documentColumnsProvider.getDocumentColumns());

  protected readonly documents = computed(() => this.detailsDocumentsStore.documents());
  protected readonly documentTypes = computed(() => this.detailsDocumentsStore.documentTypes());
  protected readonly componentLoading = computed(() => this.detailsDocumentsStore.isLoading());
  protected readonly isComponentOnError = computed(() => this.detailsDocumentsStore.error() != null);
  protected readonly errorMessage = computed(() => this.detailsDocumentsStore.error());

  constructor() {
    effect(() => {
      this.detailsDocumentsStore.loadDocuments(this.trackingNumber());
    });
  }

  protected async openUploadDialog(): Promise<void> {
    const handlingUnits = this.getHandlingUnits(this.details().packages);
    const selected = handlingUnits.find((x) => x.trackingNumber === this.trackingNumber());
    const dialogRef = this.dialogService.showDialog(UploadDocumentDialogComponent, {
      header: this.translateService.instant('pages.transport.upload-dialog.headline'),

      inputValues: {
        allowedDocumentTypes: this.documentTypes(),
        existingDocuments: this.documents(),
        initialHandlingUnit: selected,
        handlingUnits: handlingUnits,
      },
    });

    const result = await getDialogResult(dialogRef);
    if (result) {
      this.detailsDocumentsStore.loadDocuments(this.trackingNumber());
    }
  }

  protected onDownloadDocument(document: DocumentDto): void {
    this.documentsService.getDecryptedDownloadUrl(document.encryptedDownloadUrl).subscribe({
      next: (url: string) => {
        this.tracker.trackEvent(this.trackerCategory, 'Document download completed (clicked saved)');
        window.open(url + '&disposition=attachment', '_blank');
      },
      error: (err: HttpError) => {
        console.error('Download failed:', err);
      },
    });
  }

  protected onViewDocument(document: DocumentDto): void {
    this.documentsService.getDecryptedDownloadUrl(document.encryptedDownloadUrl).subscribe({
      next: (url: string) => {
        window.open(url + '&disposition=inline', '_blank');
      },
      error: (err: HttpError) => {
        console.error('View document failed:', err);
      },
    });
  }

  private getHandlingUnits(packages: ReadonlyArray<PackageDto>): HandlingUnit[] {
    const distinct = this.distinctBy(packages, 'trackingNumber');
    return distinct.map((packageUnit: PackageDto) => ({
      transportId: packageUnit.id,
      trackingNumber: packageUnit.trackingNumber,
    }));
  }

  private distinctBy<T>(array: ReadonlyArray<T>, selector: keyof T): T[] {
    const items = new Map<unknown, T>();

    for (const item of array) {
      const key = item[selector];

      if (!items.has(key)) {
        items.set(key, item);
      }
    }

    return [...items.values()];
  }
}
